import { memo } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  useTheme,
  alpha,
} from "@mui/material";
import { Link } from "react-router-dom";
import { MenuRounded } from "@mui/icons-material";
import PropTypes from "prop-types";
import { CONFIG } from "../../config";

// Custom components
import useNavBar from "./useNavBar";
import SidebarMenu from "./SidebarMenu";

/**
 * Main navigation bar container component
 */
const NavBarContainer = ({ handleCollapsed }) => {
  const theme = useTheme();

  // Get state and handlers from custom hook
  const {
    isMobile,
    rotate,
    anchorElSidebar,
    handleCloseSidebar,
    handleCollapsedMenu,
    setAnchorElSidebar,
  } = useNavBar(handleCollapsed);

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        backdropFilter: "blur(10px)",
        borderBottom:
          theme.palette.mode === "dark"
            ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
            : "none",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "2px",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              : `linear-gradient(90deg, ${alpha("#fff", 0.3)}, ${alpha("#fff", 0.1)})`,
        },
      }}
    >
      <Toolbar>
        {/* Menu/Collapse Button */}
        <Box
          onClick={(event) => {
            isMobile
              ? setAnchorElSidebar(event.currentTarget)
              : handleCollapsedMenu();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: 2,
            mr: 2,
            cursor: "pointer",
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha("#fff", 0.15),
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha("#fff", 0.25),
            },
          }}
        >
          <MenuRounded
            style={{ transform: rotate, transition: "all 0.2s linear" }}
            sx={{
              fontSize: 22,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.main
                  : "#fff",
            }}
          />
        </Box>

        {/* Sidebar Menu for Mobile */}
        <SidebarMenu
          anchorEl={anchorElSidebar}
          open={!!anchorElSidebar}
          onClose={handleCloseSidebar}
        />

        {/* Logo/Site Title */}
        <Box sx={{ flexGrow: 1 }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.5px",
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.main
                    : "#fff",
                transition: "opacity 0.2s ease",
                "&:hover": {
                  opacity: 0.85,
                },
              }}
            >
              {CONFIG.siteTitle}
            </Typography>
          </Link>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

NavBarContainer.propTypes = {
  handleCollapsed: PropTypes.func.isRequired,
};

export default memo(NavBarContainer);
